<template>
  <bm-dialog
    :label="$m.qctests_record_results"
    :width="800"
    @accept="saveHandler"
    @cancel="cancel"
  >
    <v-container fluid pa-0>
      <template v-if="filteredProductionQcMeasurementHeaders.length === 0">
        <v-row>
          <v-col cols="12">{{ $m.qctests_no_qc_tests }}</v-col>
          <v-col cols="12">
            <router-link :to="linkToAssembly">
              {{ $m.qctests_open_to_add }}
            </router-link>
          </v-col>
        </v-row>
      </template>
      <template v-else>
        <v-row>
          <v-col v-if="vessels.length > 0">
            <bm-dropdown
              :value.sync="vesselId"
              :items="vessels"
              item-value="id"
              item-text="name"
              :t="$m.keys.qctests_result_record_vessel"
              clearable
            ></bm-dropdown>
          </v-col>
          <v-col>
            <bm-text-area :value.sync="notes" :t="$m.keys.notes" />
          </v-col>
        </v-row>

        <template
          v-for="measurementHeader in filteredProductionQcMeasurementHeaders"
        >
          <v-row :key="measurementHeader.assemblyQcTestId.toString() + '1'">
            <v-col>
              <div class="bm-headline">
                {{ measurementHeader.name }}
              </div>
            </v-col>
          </v-row>
          <v-row :key="measurementHeader.assemblyQcTestId.toString() + '2'">
            <v-col
              v-for="testMeasurement in measurementHeader.measurementLines"
              :key="
                testMeasurement.metricDefinition.qcTestMetricLineId.toString()
              "
              cols="12"
              sm="6"
              md="4"
            >
              <bm-enum-ddl
                v-if="testMeasurement.metricDefinition.type.isCustomBoolean()"
                :value="
                  testMeasurement.valueBoolean === null
                    ? null
                    : testMeasurement.valueBoolean
                      ? EnumYesNoEither.yes
                      : EnumYesNoEither.no
                "
                :label="testMeasurement.metricDefinition.name"
                :enum-type="EnumYesNoEither"
                :enum-filter="customBooleanEnumFilter"
                :rules="
                  rulesForMeasurementControl(
                    measurementHeader,
                    testMeasurement,
                    false
                  )
                "
                clearable
                @update:value="
                  updateMeasurementValueYesNoEither(
                    measurementHeader,
                    testMeasurement,
                    $event
                  )
                "
              />
              <bm-text
                v-else-if="
                  testMeasurement.metricDefinition.type.isCustomString()
                "
                :value="testMeasurement.valueString"
                :label="testMeasurement.metricDefinition.name"
                :rules="
                  rulesForMeasurementControl(
                    measurementHeader,
                    testMeasurement,
                    false
                  )
                "
                @update:value="
                  updateMeasurementValue(
                    measurementHeader.assemblyQcTestId,
                    testMeasurement.metricDefinition.qcTestMetricLineId,
                    {
                      value: null,
                      valueBoolean: null,
                      valueString: $event,
                    }
                  )
                "
              />
              <bm-number-q
                v-else
                :value="testMeasurement.value"
                :label="testMeasurement.metricDefinition.name"
                :min-decimal-places="0"
                :max-decimal-places="
                  testMeasurement.metricDefinition.maximumDecimalPlaces ||
                  undefined
                "
                :min="
                  testMeasurement.metricDefinition.minimumPermittedValue ||
                  undefined
                "
                :max="
                  testMeasurement.metricDefinition.maximumPermittedValue ||
                  undefined
                "
                :default-value="null"
                clearable
                :rules="
                  rulesForMeasurementControl(measurementHeader, testMeasurement)
                "
                :suffix="
                  testMeasurement.metricDefinition.unitSuffix || undefined
                "
                :max-suffix-length="22"
                style="min-width: 150px"
                @update:value="
                  updateMeasurementValue(
                    measurementHeader.assemblyQcTestId,
                    testMeasurement.metricDefinition.qcTestMetricLineId,
                    {
                      value: $event,
                      valueBoolean: null,
                      valueString: null,
                    }
                  )
                "
              ></bm-number-q>

              <!-- If measurement is below expected minimum -->
              <bm-alert
                v-if="
                  testMeasurement.value !== null &&
                  testMeasurement.expectations.minimumExpectedValue !== null &&
                  testMeasurement.value <
                    testMeasurement.expectations.minimumExpectedValue
                "
                type="warning"
              >
                {{
                  translateWithParams("qctests_entry_below_expected_min", {
                    minValue:
                      testMeasurement.expectations.minimumExpectedValue.toString(),
                  })
                }}
              </bm-alert>

              <!-- If measurement is above expected maximum -->
              <bm-alert
                v-if="
                  testMeasurement.value !== null &&
                  testMeasurement.expectations.maximumExpectedValue !== null &&
                  testMeasurement.value >
                    testMeasurement.expectations.maximumExpectedValue
                "
                type="warning"
              >
                {{
                  translateWithParams("qctests_entry_exceeds_expected_max", {
                    maxValue:
                      testMeasurement.expectations.maximumExpectedValue.toString(),
                  })
                }}
              </bm-alert>
            </v-col>
          </v-row>
          <v-divider
            :key="measurementHeader.assemblyQcTestId.toString() + '3'"
            class="mb-2"
          />
        </template>
        <v-row>
          <v-col>
            <bm-dropdown
              :value.sync="userId"
              :items="users"
              item-text="name"
              item-value="id"
              :rules="[$ruleSet.required()]"
              :t="$m.keys.qctests_result_record_user"
            />
          </v-col>
          <v-col>
            <bm-date-time
              :value.sync="measuredAt"
              :rules="[$ruleSet.required()]"
              :t="$m.keys.qctests_result_record_measured_at"
            />
          </v-col>
        </v-row>
      </template>
    </v-container>
    <template #controls-confirm-text>
      {{ filteredProductionQcMeasurementHeaders.length ? $m.save : $m.ok }}
    </template>
  </bm-dialog>
</template>

<script setup lang="ts">
import BmDialog from "@/components/dialogs/BmDialog.vue";
import { computed, toRef } from "vue";
import type Guid from "@/app/types/common/guid";
import BmDropdown from "@/components/controls/input/BmDropdown.vue";
import BmNumberQ from "@/components/controls/input/BmNumberQ.vue";
import { useRuleSets } from "@/plugins/validation";
import type {
  ProductionQcMeasurementHeader,
  ProductionQcMeasurementLine,
} from "@/components/pages/production/qctests/composables/useCaptureAssemblyQcMeasurements";
import { useCaptureAssemblyQcMeasurements } from "@/components/pages/production/qctests/composables/useCaptureAssemblyQcMeasurements";
import BmDateTime from "@/components/controls/input/BmDateTime.vue";
import {
  useReactiveDialogScriptSetup,
  type DialogEvents,
} from "@/components/mixins/dialogs";
import type { Location } from "vue-router";
import { productionMenu } from "@/app/ui/navigation/productionMenu";
import type { AssemblyQcMeasurement } from "@/app/services/generated/assemblyqcmeasurementService";
import BmAlert from "@/components/controls/BmAlert.vue";
import { translateWithParams } from "@/plugins/i18n";
import { EnumYesNoEither } from "@/app/services/generated/enums/enumYesNoEither.generated";
import BmEnumDdl from "@/components/controls/input/BmEnumDdl.vue";
import type BaseEnumType from "@/app/services/baseEnumType";
import type { Maybe } from "@/components/utils/typeHelpers";

export interface QcTestCaptureAssemblyMeasurementsDialogProps {
  assemblyId?: Guid;
  initialVesselId?: Guid;
  assemblyQcMeasurementToEdit?: AssemblyQcMeasurement;
  assemblyQcTestIdToRecord?: Maybe<Guid>;
}

export interface QcTestCaptureAssemblyMeasurementsDialogEvents extends DialogEvents {}

/**
 * Add qc test results to an assembly.
 *
 * Designed to be fully self-contained for ease of use in all contexts (e.g. vessel summmary).
 */
const props = defineProps<QcTestCaptureAssemblyMeasurementsDialogProps>();
const emit = defineEmits<QcTestCaptureAssemblyMeasurementsDialogEvents>();

const { ruleSet } = useRuleSets();
const { accept, cancel } = useReactiveDialogScriptSetup(emit);

const {
  vessels,
  saveMeasurements,
  vesselId,
  measuredAt,
  updateMeasurementValue,
  userId,
  users,
  notes,
  filteredProductionQcMeasurementHeaders,
} = useCaptureAssemblyQcMeasurements(
  toRef(props, "assemblyId"),
  toRef(props, "initialVesselId"),
  toRef(props, "assemblyQcMeasurementToEdit"),
  props.assemblyQcTestIdToRecord
);

const updateMeasurementValueYesNoEither = (
  measurementHeader: ProductionQcMeasurementHeader,
  testMeasurement: ProductionQcMeasurementLine,
  value: BaseEnumType<EnumYesNoEither> | BaseEnumType<EnumYesNoEither>[] | null
) => {
  updateMeasurementValue(
    measurementHeader.assemblyQcTestId,
    testMeasurement.metricDefinition.qcTestMetricLineId,
    {
      value: null,
      valueBoolean:
        value === null || Array.isArray(value)
          ? null
          : (value as EnumYesNoEither).isYes(),
      valueString: null,
    }
  );
};

const rulesForMeasurementControl = (
  testRecord: ProductionQcMeasurementHeader,
  testMeasurement: ProductionQcMeasurementLine,
  numeric = true
) => {
  const rules = numeric
    ? [
        ruleSet.numberInRange(
          testMeasurement.metricDefinition.minimumPermittedValue ?? undefined,
          testMeasurement.metricDefinition.maximumPermittedValue ?? undefined
        ),
      ]
    : [];

  // If we have any results stored against this test then mark all controls in the test as required
  if (
    testRecord.measurementLines.some(
      (measurement) =>
        measurement.value !== null ||
        measurement.valueBoolean !== null ||
        measurement.valueString !== null
    )
  ) {
    rules.push(ruleSet.required());
  }

  return rules;
};

const saveHandler = async () => {
  await saveMeasurements();
  accept();
};

const linkToAssembly = computed((): Location => {
  return {
    name: productionMenu.assembly.view,
    params: {
      id: props.assemblyId?.toString() ?? "",
    },
  };
});

const customBooleanEnumFilter = (val: BaseEnumType) =>
  !(val as EnumYesNoEither).isEither();
</script>
